import Container from "../../../components/element/container";
import {
  getVehicleValue,
  formatVehicleNumber,
  formatFuelType,
} from "../../../helpers/vehicle.specifications";
import {
  getInspectionTotals,
  getInspectionGrades,
} from "../../../helpers/inspection.report";
import { gallerySlide, reportMedia } from "../../../helpers/gallery.media";
import { downloadInspectionReport } from "../../../helpers/download.inspection.report";
import { useDispatch, useSelector } from "react-redux";
import useAppliedReferral from "../../../components/hook/use.applied.referral";
import {
  openGalleryModal,
  openReportModal,
  openReferralModal,
  openReservationModal,
} from "../../../redux/reducer/modal.reducer";
import FullModal from "../../../components/modal/full.modal";
import { MDBModalBody } from "mdb-react-ui-kit";
import Image from "../../../components/element/image";
import Col from "../../../components/element/col";
import Row from "../../../components/element/row";
import Button from "../../../components/element/button";
import Icon from "../../../components/element/icon";
import formatNumber from "../../../helpers/format.number";
import { showToast } from "../../../helpers/show.toast";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCreative, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/effect-creative";
import "swiper/css/pagination";
import { useEffect, useRef, useState } from "react";
import {
  getProductList,
  getProductInfo,
  setGalleryTab,
  setProductMedia,
} from "../../../redux/reducer/product.reducer";
import {
  openFullSizeGallery,
  setFullSizeGallery,
} from "../../../redux/reducer/common.reducer";
import { DcLogo, ShowCarPlate } from "../../../assets/images";
import { Skeleton } from "primereact/skeleton";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { useTranslation } from "react-i18next";
import CommonProduct from "../../../components/common/common.product";
import CommonModalHeader from "../../../components/common/common.modal.header";
import CommonInspectionSummary from "../../../components/common/common.inspection.summary";
import { getExteriorGallery } from "../../../helpers/product.media";
import useWindowDimension from "../../../components/hook/use.window.dimension";
import ReferAndEarnModal from "./refer.and.earn.modal";
import ReserveVehicleModal from "./reserve.vehicle.modal";
import FinancingSection from "../components/financing.section";
import useRequireAuth from "../../../components/hook/use.require.auth";

export default function ProductModal() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const gallerySwiperRef = useRef(null);
  const location = useLocation();
  const { t } = useTranslation();
  const { id } = useParams();

  const { width, isDesktop } = useWindowDimension();
  const { requireAuth } = useRequireAuth();
  const {
    productInfo,
    productInfoLoading,
    productMedia,
    filterData,
    productList,
  } = useSelector((state) => state.product);
  const { username } = useSelector((state) => state.common);
  const { profileInfo } = useSelector((state) => state.profile);
  const productStatus = String(
    productInfo?.status?.name ??
      productInfo?.status?.value ??
      productInfo?.status ??
      productInfo?.availability_status ??
      productInfo?.product_status ??
      "",
  ).toLowerCase();
  const isReserved = productStatus === "reserved";
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [openSections, setOpenSections] = useState({
    specifications: true,
    features: true,
    carReport: true,
  });

  const specificationValue = (keys, fallback = "-") => {
    for (const key of keys) {
      const value = getVehicleValue(productInfo, key);
      if (value !== "-") return value;
    }
    return fallback ?? "-";
  };
  const fuelType = specificationValue(["fuel_type"]);
  const normalizedFuelType = String(fuelType).toLowerCase();
  const vehicleType = /phev|plug[ _-]?in/.test(normalizedFuelType)
    ? "phev"
    : /hybrid|hev/.test(normalizedFuelType) && !/bev/.test(normalizedFuelType)
      ? "hybrid"
      : /electric|^ev|^bev/.test(normalizedFuelType)
        ? "ev"
        : "petrol";
  const formattedMileage = formatVehicleNumber(
    specificationValue(["mileage"]),
    "km",
  );

  const handleCloseProduct = () => {
    // Opened straight from a link, there is nothing to go back to, so going
    // back would leave the site.
    if (location.key === "default") {
      navigate("/");
      return;
    }

    navigate(-1);
  };

  const handleToggleSection = (section) => {
    setOpenSections((currentSections) => ({
      ...currentSections,
      [section]: !currentSections[section],
    }));
  };

  const askList = ["ask for best price", "ask car report", "ask for visit"];

  const commonOverview = [
    {
      icon: "solar:calendar-linear",
      title: "Registration",
      value: specificationValue(["register_year"]),
    },
    {
      icon: "qlementine-icons:meter-high-16",
      title: "Mileage",
      value: formattedMileage,
    },
    {
      icon: "solar:calendar-linear",
      title: "Insurance Claims",
      value: specificationValue(["insurance_claims"]),
    },
  ];

  const typeOverview = {
    ev: [
      ["solar:battery-charge-linear", "Battery SOH", ["battery_soh"]],
      ["mdi:fuel-pump-outline", "Drive Range", ["drive_range"], "km"],
      ["solar:bolt-bold", "Motor Power", ["engine_capacity_cc"], "kW"],
    ],
    petrol: [
      ["mdi:engine-outline", "Engine", ["engine"]],
      [
        "game-icons:gear-stick-pattern",
        "Transmission",
        ["transmission", "transmission_type"],
      ],
      ["solar:bolt-bold", "Engine Capacity", ["engine_capacity_cc"], "cc"],
    ],
    phev: [
      ["mdi:engine-outline", "Engine", ["engine"]],
      ["mdi:fuel-pump-outline", "Drive Range", ["drive_range"], "km"],
      ["solar:bolt-bold", "Engine Capacity", ["engine_capacity_cc"], "cc"],
    ],
    hybrid: [
      ["mdi:engine-outline", "Engine", ["engine"]],
      [
        "game-icons:gear-stick-pattern",
        "Transmission",
        ["transmission", "transmission_type"],
      ],
      ["solar:bolt-bold", "Engine Capacity", ["engine_capacity_cc"], "cc"],
    ],
  };
  const overviewList = [
    ...commonOverview,
    ...typeOverview[vehicleType].map(([icon, title, keys, unit]) => ({
      icon,
      title,
      value: unit
        ? formatVehicleNumber(specificationValue(keys), unit)
        : specificationValue(keys),
    })),
  ];

  const specList = [
    ["Model Year", "year"],
    ["Make", "brand.name"],
    ["Model", "car_model.name"],
    ["Spec", "spec"],
    ["Fuel Type", "fuel_type"],
    ["Body Type", "series"],
    ["Battery Capacity", "battery_capacity_kwh", "kWh"],
    ["Battery Type", "battery_type"],
    ["Drivetrain", "drive_train"],
    ["Fast Charge", "fast_charge", "kW"],
    ["Rim Size", "rim_size", "inch"],
    ["Seats", "seat_capacity"],
    ["Exterior Color", "color"],
    ["Interior Color", "interior_color"],
  ].map(([label, key, unit]) => {
    const value = getVehicleValue(productInfo, key);
    return {
      label,
      value:
        key === "fuel_type"
          ? formatFuelType(value)
          : unit
            ? formatVehicleNumber(value, unit)
            : value,
    };
  });

  const featureList = productInfo?.info;

  const mediaKey = {
    all_image: [],
    exterior_image: [],
    interior_image: [],
    panorama_image: [],
    imperfection_image: [],
    inspection_report: [],
    inspection_defect: [],
    car_report: [],
    video: [],
  };

  const mediaType = {
    exterior_image: "exterior_image",
    interior_image: "interior_image",
    "360_image": "panorama_image",
    imperfection_image: "imperfection_image",
    inspection_report: "inspection_report",
    inspection_defect: "inspection_defect",
    car_report: "car_report",
    video: "video",
  };

  const thumbnailList = [
    {
      title: "Exterior",
      key: "exterior_image",
    },
    {
      title: "Interior",
      key: "interior_image",
    },
    {
      title: "Details",
      key: "imperfection_image",
    },
    {
      title: "360",
      key: "panorama_image",
    },
  ];

  // Only categories that actually have images earn a tile, in the order above,
  // and at most three so the row keeps room for the "+N" tile.
  const availableThumbnails = thumbnailList.filter(
    (thumbnail) => (productMedia[thumbnail.key] || []).length > 0,
  );
  const shownThumbnails = availableThumbnails.slice(0, 3);
  // A tile already reaches every image in its own category, and the main image
  // is the big one above, so "+N" is only what the hidden categories hold.
  const hiddenThumbnails = availableThumbnails.slice(3);
  const remainingImageCount = hiddenThumbnails.reduce(
    (total, thumbnail) => total + (productMedia[thumbnail.key] || []).length,
    0,
  );

  const tagList = {
    "DCAR Certified":
      "Officially inspected with live verification to ensure authenticity and condition transparency.",
    "GOOD Deals":
      "Special priced & limited offer with better-than-average pricing.",
  };

  const handleRedirect = (type) => {
    const AGENT_WHATSAPP = productInfo?.agent?.phone_number;

    if (!AGENT_WHATSAPP) return;

    if (type === "whatsapp") {
      const carDetails = [
        productInfo?.year,
        productInfo?.brand?.name,
        productInfo?.car_model?.name,
        productInfo?.spec,
      ]
        .filter(Boolean)
        .join(" ");
      const message =
        `Hi DCar, I am interested in: ${productInfo?.name || "-"}` +
        `${carDetails ? ` [${carDetails}]` : ""}\n` +
        `URL: ${window.location.href}`;

      const encodedMessage = encodeURIComponent(message);

      window.open(
        `https://wa.me/${AGENT_WHATSAPP}?text=${encodedMessage}`,
        "_blank",
      );
    } else {
      window.location.href = `tel:${AGENT_WHATSAPP}`;
    }
  };

  const handleShare = (copyOnly = false) => {
    if (productInfo?.id) {
      const profileReferral =
        profileInfo?.referral || profileInfo?.referral_info || {};
      const referralCode =
        profileReferral?.code ||
        profileInfo?.referral_code?.code ||
        profileInfo?.referral_code ||
        profileInfo?.code ||
        localStorage.getItem("own_referral_code");
      const referralName =
        profileInfo?.name ||
        profileInfo?.username ||
        profileInfo?.user?.name ||
        profileInfo?.user?.username ||
        localStorage.getItem("name") ||
        username;
      const productUrl = new URL(
        `/${
          productInfo?.category == "car plate" ? "car plate" : "car"
        }/${productInfo?.id}`,
        process.env.REACT_APP_URL || window.location.origin,
      );

      if (referralCode) {
        productUrl.searchParams.set("referral_code", referralCode);
        if (referralName) {
          productUrl.searchParams.set("referrer", referralName);
        }
      }

      const productName = productInfo.name;

      if (copyOnly) {
        const copyLink = async () => {
          try {
            await navigator.clipboard.writeText(productUrl.toString());
            showToast(
              "Link copied",
              "Referral link copied to clipboard.",
              "success",
            );
          } catch {
            showToast(
              "Unable to copy link",
              "Please try again or use Share with Friends.",
              "error",
            );
          }
        };
        return copyLink();
      }

      if (navigator.share) {
        navigator
          .share({
            text: `Hey friends, just came across this ${
              productInfo?.category === "car plate" ? "car plate" : "car"
            } — thought you might want to check it out 👀\n\n${productName}: ${productUrl.toString()}`,
          })
          .then(() =>
            showToast(
              "Ready to share",
              "Choose where you'd like to share",
              "success",
            ),
          )
          .catch((error) => {
            if (error?.name !== "AbortError") {
              const fallbackMessage = `Check out this ${
                productInfo?.category == "car plate" ? "car plate" : "car"
              }: ${productName}\n${productUrl.toString()}`;
              navigator.clipboard.writeText(fallbackMessage).then(() => {
                showToast(
                  "Sharing not supported",
                  "Link copied to clipboard. Share it anywhere you like.",
                  "error",
                );
              });
            }
          });
      } else {
        const fallbackMessage = `Check out this ${
          productInfo?.category == "car plate" ? "car plate" : "car"
        }: ${productName}\n${productUrl.toString()}`;
        navigator.clipboard.writeText(fallbackMessage).then(() => {
          showToast(
            "Sharing not supported",
            "Link copied to clipboard. Share it anywhere you like.",
            "error",
          );
        });
      }
    }
  };

  const handleOpenGallery = (media, index) => {
    const gallery = (media || []).filter(item => item?.url).map(gallerySlide);

    if (gallery.length > 0) {
      const sortedGallery = [
        ...gallery.slice(index),
        ...gallery.slice(0, index),
      ];

      dispatch(openFullSizeGallery());
      dispatch(setFullSizeGallery(sortedGallery));
    }
  };

  const isCarPlate = productInfo?.category === "car plate";
  const {
    code: referredCode,
    name: referredBy,
    cancel: cancelReferral,
  } = useAppliedReferral();
  const inspectionInfo = productInfo?.inspection_report || {};
  const finalPrice = productInfo?.price;
  const oriPrice = productInfo?.final_price;
  const newPrice = productInfo?.new_car_price;
  const servicePrices = productInfo?.service_prices || {};
  const reservationFee = Number(servicePrices.reservation ?? 5000);
  const handlingPrice = Number(productInfo?.handling_fees ?? 3000);
  // The product carries a calculated road tax; the service price list is the
  // fallback for records that have none.
  const roadTaxPrice =
    productInfo?.road_tax?.annual_rm ?? servicePrices.road_tax ?? "";
  const insurancePrice = productInfo?.est_insurance ?? "";
  const conditioningPrice = productInfo?.service_prices?.conditioning || 0;
  const warrantyPrice = productInfo?.service_prices?.warranty | 0;
  const inspectionSummary = getInspectionTotals(inspectionInfo);
  const conditionList = getInspectionGrades(inspectionInfo);
  const inspectionPointTotal = inspectionSummary.total;
  const inspectionWorkshop =
    inspectionInfo?.workshop_name ||
    inspectionInfo?.workshop?.name ||
    inspectionInfo?.technician_name ||
    "DHub";
  const inspectionReference =
    inspectionInfo?.reference_no ||
    inspectionInfo?.inspection_id ||
    inspectionInfo?.id ||
    "-";
  const inspectionSkipped = String(productInfo?.inspection_status || "").toLowerCase() === "skipped";
  const inspectionDefects = productMedia?.inspection_defect || [];
  const carReports = productMedia?.car_report || [];
  const inspectionThumbnails = inspectionDefects.slice(0, 3);

  const handleOpenInspection = (focus) => {
    if (inspectionInfo?.engine_system) {
      dispatch(openReportModal(focus || null));
      return;
    }

    handleOpenGallery(productMedia?.inspection_report, 0);
  };

  const handleDownloadReport = async () => {
    if (downloadingReport) return;
    setDownloadingReport(true);
    try {
      const { failedPhotos } = await downloadInspectionReport(productInfo);
      if (failedPhotos) {
        showToast(
          "Report downloaded",
          `${failedPhotos} photo(s) could not be loaded and are marked in the report.`,
          "warning",
        );
      }
    } catch (error) {
      showToast(
        "Unable to download report",
        error.message || "Please try again.",
        "error",
      );
    } finally {
      setDownloadingReport(false);
    }
  };

  const listedProducts = Array.isArray(productList)
    ? productList
    : Object.values(productList || {}).flatMap((entry) =>
        Array.isArray(entry) ? entry : [],
      );
  const listedProduct = listedProducts.find(
    (product) => String(product?.id) === String(productInfo?.id || id),
  );
  const fallbackProductImage =
    listedProduct?.media?.[0]?.url ||
    productInfo?.thumbnail ||
    productInfo?.image ||
    DcLogo;
  const similarProducts = [
    ...Object.values(filterData || {}).flatMap((entry) =>
      Array.isArray(entry?.data) ? entry.data : [],
    ),
    ...listedProducts,
  ]
    .filter(
      (product, index, products) =>
        product?.id !== productInfo?.id &&
        products.findIndex((item) => item?.id === product?.id) === index,
    )
    .slice(0, 4);
  const hasInspectionReport = Boolean(
    inspectionInfo?.inspection_grade || productMedia?.inspection_report?.length,
  );

  const handleOpenCarReport = () => handleOpenGallery(carReports, 0);

  const productLocation = productInfo?.view_location;
  const listingIdentity = (
    <Row className="_row listing-identity">
      {productInfo?.tag?.length > 0 && (
        <article
          className="_row info-tag"
          data-tooltip-id="tag-tooltip"
          data-tooltip-content={t(tagList[productInfo.tag[0]])}
        >
          {productInfo?.tag[0]?.toLowerCase() === "dcar certified" && (
            <Icon
              tag="span"
              icon="streamline-ultimate:car-3-bold"
              className="_svg certified-tag"
              gradient
              width={12}
              height={12}
            />
          )}
          <p className="_label info-tag-title">
            {t(
              productInfo.tag[0]?.toLowerCase() === "dcar certified"
                ? "DCAR Certified"
                : productInfo.tag[0],
            )}
          </p>
          <Tooltip place="top-start" id="tag-tooltip" positionStrategy="top" />
        </article>
      )}
      {productLocation && (
        <Row className="_row listing-location">
          <Icon icon="f7:location-fill" className="_svg location" />
          <p className="_label product-location">{productLocation}</p>
        </Row>
      )}
      {carReports.length > 0 && (
        <button
          type="button"
          className="car-report-link pointer"
          aria-label={t("Car Report")}
          onClick={handleOpenCarReport}
        >
          <Icon
            tag="span"
            icon="solar:document-text-linear"
            className="_svg car-report-link"
          />
          <span className="_label car-report-link">{t("Car Report")}</span>
        </button>
      )}
    </Row>
  );

  useEffect(() => {
    // if (String(productInfo?.id) !== String(id)) {
    dispatch(
      getProductInfo({
        id: id,
      }),
    );
    // }
  }, [id]);

  useEffect(() => {
    if (width >= 768) {
      dispatch(
        getProductList({
          start: 0,
          length: 8,
          category: "",
        }),
      );
    }
  }, [dispatch, id, width]);

  useEffect(() => {
    const normalizedMedia = (productInfo?.media ?? [])
      .map((image) => ({
        ...image,
        url:
          image?.url ||
          image?.original_url ||
          image?.file_url ||
          image?.full_url ||
          image?.path,
      }))
      .filter((image) => Boolean(image.url));

    const grouped = normalizedMedia.reduce(
      (acc, image) => {
        const key = mediaType[image.collection_type];
        if (!key) return acc;

        acc[key] = [...acc[key], image];
        return acc;
      },
      { ...mediaKey },
    );

    grouped.car_report = reportMedia(productInfo, "car_report");
    grouped.inspection_report = reportMedia(productInfo, "inspection_report");
    grouped.exterior_image = getExteriorGallery(grouped.exterior_image);

    const productMediaData = {
      ...grouped,
      all_image: [
        ...grouped.exterior_image,
        ...grouped.interior_image,
        ...grouped.imperfection_image,
      ],
    };

    dispatch(setProductMedia(productMediaData));
  }, [productInfo?.media, productInfo?.car_report, productInfo?.inspection_report]);

  const productModalFooter = (
    <Col className="_col contact-info">
      {productInfoLoading ? (
        <Row className="_row footer-skeleton">
          <Skeleton
            height="32px"
            width="50%"
            borderRadius="3px"
            className="skeleton"
          />
          <Skeleton
            height="32px"
            width="50%"
            borderRadius="3px"
            className="skeleton"
          />
        </Row>
      ) : (
        <>
          {!isCarPlate && (
            <Row className="_row ask-list">
              {askList.map((ask, askIndex) => (
                <Row className="_row ask-item-wrap" key={askIndex}>
                  <Icon
                    icon="mdi:comment-processing-outline"
                    className="_svg ask"
                  />
                  <p className="_label ask-item">{t(ask)}</p>
                </Row>
              ))}
            </Row>
          )}
          {!isReserved && (
            <Row className="_row action-list">
              <Button
                className="_button whatsapp footer-action reserve-action"
                onClick={() =>
                  requireAuth(() => dispatch(openReservationModal()), {
                    modal: "reservation",
                  })
                }
              >
                {width > 400 && (
                  <Icon
                    icon="solar:card-linear"
                    className="_svg action reserve"
                  />
                )}
                <Col className="_col action-copy">
                  <p className="_label action-title">{t("Pay & Reserve")}</p>
                  <p className="_label action-subtitle">
                    {t("RM5000 Refundable")}
                  </p>
                </Col>
              </Button>
              <Button
                className="_button call footer-action contact-action"
                onClick={() => handleRedirect("whatsapp")}
              >
                {width > 400 && (
                  <Icon
                    icon="logos:whatsapp-icon"
                    className="_svg action contact"
                  />
                )}
                <Col className="_col action-copy">
                  <p className="_label action-title contact-action-title">
                    {t("Ask for Test Drive")}
                  </p>
                  <p className="_label action-subtitle contact-action-subtitle">
                    {t("9AM - 9PM Daily")}
                  </p>
                </Col>
              </Button>
            </Row>
          )}
        </>
      )}
    </Col>
  );

  const inspectionContent = (
    <Col className="_col inspection-overview">
      {(inspectionSkipped
        ? productMedia?.inspection_report?.length > 0
        : hasInspectionReport) && (
        <>
          {!inspectionSkipped && (
            <CommonInspectionSummary
              t={t}
              grade={inspectionInfo?.inspection_grade}
              grades={conditionList}
              workshop={inspectionWorkshop}
              pointTotal={inspectionPointTotal}
              reference={inspectionReference}
              passed={inspectionSummary.passed}
              identified={inspectionSummary.identified}
              onSelectGrade={(grade) => handleOpenInspection({ grade })}
              onSelectTally={(filter) => handleOpenInspection({ filter })}
              onOpenReport={() => handleOpenInspection()}
            />
          )}

          {!inspectionSkipped && inspectionThumbnails.length > 0 && (
            <Row className="_row inspection-thumbnails">
              {inspectionThumbnails.map((media, index) => (
                <Col
                  className="_col inspection-thumbnail pointer"
                  key={media?.url || index}
                  onClick={() => handleOpenGallery(inspectionDefects, index)}
                >
                  <Image
                    src={media?.url}
                    alt={media?.action || t("Inspection photo")}
                    className="_img inspection-thumbnail"
                  />
                </Col>
              ))}
              {inspectionDefects.length > 3 && (
                <button
                  type="button"
                  className="inspection-thumbnail-more pointer"
                  onClick={() => handleOpenGallery(inspectionDefects, 3)}
                >
                  {t("View Details")}
                </button>
              )}
            </Row>
          )}

          <Button
            className="_button view-report"
            onClick={handleDownloadReport}
            disabled={downloadingReport}
          >
            <Icon
              icon="solar:download-minimalistic-linear"
              className="_svg view-report"
            />
            <span className="_label view-report">
              {t(
                downloadingReport
                  ? "Preparing report…"
                  : "Download Full Report",
              )}
            </span>
          </Button>


        </>
      )}
    </Col>
  );

  return (
    <>
      <FullModal
        independent
        scrollable={width < 768}
        staticBackdrop={true}
        show={true}
        close={handleCloseProduct}
        screenSize="xl"
        className={`product-modal ${isCarPlate ? "car-plate" : ""}`}
        footer={width < 768 && !isReserved ? productModalFooter : null}
        content={
          <MDBModalBody>
            {!isDesktop && (
              <CommonModalHeader
                onBack={handleCloseProduct}
                onShare={() =>
                  requireAuth(() => dispatch(openReferralModal()), {
                    modal: "referral",
                  })
                }
                shareLabel={t("Share & Earn RM500")}
              />
            )}
            {referredCode && (
              <Row className="_row referral-bar">
                <Row className="_row referral-message">
                  <Icon icon="solar:gift-linear" className="_svg referral" />
                  <p className="_label referral-copy">
                    {t("Order with")}
                    <span className="_label referral-name">
                      {referredBy || t("Friend")}
                      {t("'s Referral")}
                    </span>
                  </p>
                </Row>
                <span className="referral-divider" />
                {isDesktop && (
                  <p className="_label referral-code-copy">
                    {t("Referral Code:")}
                    <span className="_label referral-code">{referredCode}</span>
                  </p>
                )}
                <Button
                  className="_button referral-share"
                  onClick={cancelReferral}
                >
                  <span className="_label referral-share">{t("Cancel")}</span>
                </Button>
              </Row>
            )}
            {productInfoLoading ? (
              <Col className="_col product-info product-modal-layout product-skeleton-layout">
                {isDesktop && (
                  <CommonModalHeader
                    onBack={handleCloseProduct}
                    onShare={() =>
                      requireAuth(() => dispatch(openReferralModal()))
                    }
                    shareLabel={t("Share & Earn RM500")}
                  />
                )}
                {isDesktop && (
                  <Row className="_row listing-identity product-skeleton-identity">
                    <Skeleton className="skeleton product-skeleton-badge" />
                    <Skeleton className="skeleton product-skeleton-location" />
                  </Row>
                )}
                <Col className="product-media-column product-skeleton-media-column w-100">
                  <Row className="relative w-100 product-skeleton-hero-wrap">
                    <Skeleton className="skeleton product-skeleton-hero" />
                  </Row>
                </Col>
                <Col className="_col basic-info product-skeleton-summary">
                  {!isDesktop && (
                    <Row className="_row listing-identity product-skeleton-identity">
                      <Skeleton className="skeleton product-skeleton-badge" />
                      <Skeleton className="skeleton product-skeleton-location" />
                    </Row>
                  )}
                  <Skeleton className="skeleton product-skeleton-title" />
                  <Skeleton className="skeleton product-skeleton-price-value" />
                  <Skeleton className="skeleton product-skeleton-notice" />
                  <Skeleton className="skeleton product-skeleton-overview" />
                </Col>
              </Col>
            ) : (
              <Col className="_col product-info product-modal-layout">
                <Container tag="div" className="product-left-column">
                  {isDesktop && (
                    <>
                      <CommonModalHeader
                        onBack={handleCloseProduct}
                        onShare={() =>
                          requireAuth(() => dispatch(openReferralModal()), {
                            modal: "referral",
                          })
                        }
                        shareLabel={t("Share & Earn RM500")}
                      />
                    </>
                  )}

                  {isCarPlate ? (
                    <Col className="relative w-100">
                      <Image
                        src={
                          productInfo?.media?.length > 0
                            ? productInfo.media?.[0]?.url
                            : ShowCarPlate
                        }
                        className="_img"
                      />
                      {productInfo?.media?.length === 0 && (
                        <h2 className="_label car-plate">{productInfo.name}</h2>
                      )}
                    </Col>
                  ) : (
                    <Col className="product-media-column flex-col gap-1 w-100">
                      <Row className="relative w-100">
                        <Row className="_row other-media">
                          {productMedia.panorama_image?.length > 0 && (
                            <Button
                              className="_button panorama"
                              onClick={() => {
                                dispatch(openGalleryModal());
                                dispatch(setGalleryTab("panorama_image"));
                              }}
                            >
                              <Icon
                                className="_svg panorama"
                                icon="line-md:round-360"
                              />
                              <p className="_label other-title">
                                {t("View 360")}
                              </p>
                            </Button>
                          )}
                          {productMedia.video?.length > 0 && (
                            <Button
                              className="_button video"
                              onClick={() => {
                                dispatch(openGalleryModal());
                                dispatch(setGalleryTab("video"));
                              }}
                            >
                              <Icon
                                className="_svg video"
                                icon="line-md:play"
                              />
                              <p className="_label other-title">{t("Video")}</p>
                            </Button>
                          )}
                        </Row>
                        {productMedia?.all_image?.length > 1 && (
                          <>
                            <Button
                              className="_button swiper-prev"
                              onClick={() =>
                                gallerySwiperRef.current?.slidePrev()
                              }
                            >
                              <Icon
                                icon="mingcute:left-line"
                                className="_svg swiper-navigation"
                              />
                            </Button>
                            <Button
                              className="_button swiper-next"
                              onClick={() =>
                                gallerySwiperRef.current?.slideNext()
                              }
                            >
                              <Icon
                                icon="mingcute:right-line"
                                className="_svg swiper-navigation"
                              />
                            </Button>
                          </>
                        )}
                        <Swiper
                          touchStartPreventDefault={false}
                          key={id}
                          className="product-swiper"
                          spaceBetween={0}
                          slidesPerView={1}
                          grabCursor={true}
                          effect={"creative"}
                          creativeEffect={{
                            prev: {
                              shadow: true,
                              translate: [0, 0, -400],
                            },
                            next: {
                              translate: ["100%", 0, 0],
                            },
                          }}
                          modules={[EffectCreative, Pagination]}
                          // The photos arrive after this mounts, so the arrows
                          // do not exist when Swiper resolves selector strings
                          // and navigation stays dead. Driving the instance
                          // directly avoids that ordering entirely.
                          onSwiper={(swiper) => {
                            gallerySwiperRef.current = swiper;
                          }}
                          pagination={{ clickable: true }}
                        >
                          {productMedia?.all_image?.length > 0 ? (
                            productMedia.all_image.map((media, mediaIndex) => (
                              <SwiperSlide
                                key={mediaIndex}
                                onClick={() =>
                                  handleOpenGallery(
                                    productMedia.all_image,
                                    mediaIndex,
                                  )
                                }
                              >
                                <Image
                                  src={media.url || fallbackProductImage}
                                  alt={productInfo?.name || ""}
                                  loading={mediaIndex === 0 ? "eager" : "lazy"}
                                  onError={(event) => {
                                    if (
                                      !event.currentTarget.dataset
                                        .fallbackApplied
                                    ) {
                                      event.currentTarget.dataset.fallbackApplied =
                                        "true";
                                      event.currentTarget.src =
                                        fallbackProductImage;
                                    }
                                  }}
                                />
                              </SwiperSlide>
                            ))
                          ) : (
                            <SwiperSlide>
                              <Image
                                src={fallbackProductImage}
                                alt={productInfo?.name || ""}
                                className="_img default"
                              />
                            </SwiperSlide>
                          )}
                        </Swiper>
                      </Row>
                      {width >= 768 ? (
                        <Row className="_row media-thumbnails">
                          {shownThumbnails.map((thumbnail) => {
                            const thumbnailImages =
                              productMedia[thumbnail.key] || [];

                            return (
                              <button
                                type="button"
                                className="thumbnail-item"
                                key={thumbnail.key}
                                style={{
                                  backgroundImage: `url("${thumbnailImages[0]?.url}")`,
                                }}
                                onClick={() => {
                                  dispatch(openGalleryModal());
                                  dispatch(setGalleryTab(thumbnail.key));
                                }}
                              >
                                <span>
                                  {t(thumbnail.title)} ({thumbnailImages.length}
                                  )
                                </span>
                              </button>
                            );
                          })}
                          {remainingImageCount > 0 && (
                            <button
                              type="button"
                              className="thumbnail-more"
                              onClick={() => {
                                dispatch(openGalleryModal());
                                dispatch(
                                  setGalleryTab(
                                    hiddenThumbnails[0]?.key || "all_image",
                                  ),
                                );
                              }}
                            >
                              +{remainingImageCount} {t("Image")}
                            </button>
                          )}
                        </Row>
                      ) : (
                        shownThumbnails.length > 0 && (
                          <Row className="_row media-thumbnails">
                            {shownThumbnails.map(
                              (thumbnail, thumbnailIndex) => (
                                <Col
                                  className="_col thumbnail-item pointer"
                                  key={thumbnailIndex}
                                  style={{
                                    backgroundImage: `url("${
                                      productMedia[thumbnail.key]?.[0]?.url ||
                                      fallbackProductImage
                                    }")`,
                                  }}
                                  onClick={() => {
                                    dispatch(openGalleryModal());
                                    dispatch(setGalleryTab(thumbnail.key));
                                  }}
                                >
                                  <Image
                                    src={
                                      productMedia[thumbnail.key]?.[0]?.url ||
                                      fallbackProductImage
                                    }
                                    alt=""
                                    className="_img thumbnail"
                                    loading="eager"
                                    onError={(event) => {
                                      if (
                                        !event.currentTarget.dataset
                                          .fallbackApplied
                                      ) {
                                        event.currentTarget.dataset.fallbackApplied =
                                          "true";
                                        event.currentTarget.src =
                                          fallbackProductImage;
                                      }
                                    }}
                                  />
                                  <p className="_label thumbnail-title">
                                    {t(thumbnail.title)} (
                                    {productMedia[thumbnail.key].length})
                                  </p>
                                </Col>
                              ),
                            )}
                          </Row>
                        )
                      )}
                    </Col>
                  )}
                  {isDesktop && !isCarPlate && inspectionContent}
                </Container>
                <Container tag="div" className="product-right-column">
                  {isDesktop && listingIdentity}
                  <Col
                    className={`_col basic-info ${
                      isCarPlate ? "car-plate-info" : ""
                    }`}
                  >
                    {!isDesktop && listingIdentity}
                    <h3 className="_label info-name">
                      {productInfo?.name ?? "2022 Tesla Model 3 Long Range"}
                    </h3>

                    {isCarPlate ? (
                      <>
                        <h2 className="_label info-price">
                          RM{" "}
                          {productInfo?.price
                            ? formatNumber(productInfo.price)
                            : "-"}
                        </h2>
                        {productInfo?.description && (
                          <p
                            className="_label info-location"
                            dangerouslySetInnerHTML={{
                              __html: productInfo.description,
                            }}
                          />
                        )}
                      </>
                    ) : (
                      <>
                        <Row className="_row price-breakdown">
                          <Col className="_col price-column final-price">
                            <h2 className="_label info-price">
                              RM{finalPrice ? formatNumber(finalPrice) : "-"}
                            </h2>
                            <Row className="_row price-caption">
                              <p className="_label final-price-label">
                                {t("Final Price")}{" "}
                                {oriPrice != finalPrice && (
                                  <del className="_label original-price">
                                    RM{formatNumber(oriPrice)}
                                  </del>
                                )}
                              </p>
                            </Row>
                          </Col>
                          <span className="price-divider" />
                          <Col className="_col price-column">
                            <h4 className="_label service-price">
                              {parseFloat(conditioningPrice) > 0
                                ? `+RM${formatNumber(conditioningPrice)}`
                                : "-"}
                            </h4>
                            <p className="_label service-title">
                              {t("Conditioning")}
                            </p>
                          </Col>
                          <span className="price-divider" />
                          <Col className="_col price-column">
                            <h4 className="_label service-price">
                              {parseFloat(warrantyPrice) > 0
                                ? `+RM${formatNumber(warrantyPrice)}`
                                : "-"}
                            </h4>
                            <p className="_label service-title">
                              {t("Warranty")}
                            </p>
                          </Col>
                        </Row>

                        {Number(newPrice) > 0 && (
                          <Row className="_row saving-note">
                            <Icon
                              icon="griddy-icons:confetti"
                              className="_svg saving-note"
                            />
                            <p className="_label saving-note">
                              {t("New Car Price")}{" "}
                              <strong>
                                RM{formatNumber(newPrice, "alphabet")}
                              </strong>
                              , {t("You save approximately")}{" "}
                              <strong>
                                RM
                                {formatNumber(
                                  newPrice - finalPrice,
                                  "alphabet",
                                )}
                              </strong>
                            </p>
                          </Row>
                        )}

                        <Row className="_row overview-grid">
                          {overviewList.map((overview) => (
                            <Col
                              className="_col overview-item"
                              key={overview.title}
                            >
                              <Icon
                                icon={overview.icon}
                                className="_svg overview"
                                gradient
                                width={21}
                                height={21}
                              />
                              <p className="_label overview-title">
                                {t(overview.title)}
                              </p>
                              <h6 className="_label overview-value">
                                {overview.value}
                              </h6>
                            </Col>
                          ))}
                        </Row>
                        {width >= 768 && productModalFooter}
                      </>
                    )}
                  </Col>
                  <Col className="product-detail-column w-100">
                    {!isCarPlate && (
                      <Col className="_col detail-info">
                        <Col className="_col product-accordion-list">
                          <Col className="_col detail-section">
                            <button
                              type="button"
                              className="_row detail-section-header pointer"
                              aria-expanded={openSections.specifications}
                              onClick={() =>
                                handleToggleSection("specifications")
                              }
                            >
                              <span className="_row detail-section-title">
                                <Icon
                                  icon="solar:settings-linear"
                                  className="_svg section-title"
                                />
                                <h6 className="_label section-title">
                                  {t("Specifications")}
                                </h6>
                              </span>
                              <Icon
                                icon={
                                  openSections.specifications
                                    ? "mingcute:up-line"
                                    : "mingcute:down-line"
                                }
                                className="_svg section-chevron"
                              />
                            </button>
                            {openSections.specifications && (
                              <Col className="_col spec-list">
                                {specList.map((spec, specIndex) => (
                                  <Row
                                    className="_row spec-item"
                                    key={specIndex}
                                  >
                                    <p className="_label spec-title">
                                      {t(spec.label)}
                                    </p>
                                    <h6 className="_label spec-value">
                                      {spec.value || "-"}
                                    </h6>
                                  </Row>
                                ))}
                              </Col>
                            )}
                          </Col>

                          {featureList?.length > 0 && (
                            <Col className="_col detail-section">
                              <button
                                type="button"
                                className="_row detail-section-header pointer"
                                aria-expanded={openSections.features}
                                onClick={() => handleToggleSection("features")}
                              >
                                <span className="_row detail-section-title">
                                  <Icon
                                    icon="solar:star-linear"
                                    className="_svg section-title"
                                  />
                                  <h6 className="_label section-title">
                                    {t("Features")}
                                  </h6>
                                </span>
                                <Icon
                                  icon={
                                    openSections.features
                                      ? "mingcute:up-line"
                                      : "mingcute:down-line"
                                  }
                                  className="_svg section-chevron"
                                />
                              </button>
                              {openSections.features && (
                                <Row className="_row feature-list">
                                  {featureList.map((feature, featureIndex) => (
                                    <p
                                      className="_label feature-item"
                                      key={featureIndex}
                                    >
                                      {feature}
                                    </p>
                                  ))}
                                </Row>
                              )}
                            </Col>
                          )}

                          {/* {productInfo?.description && (
                          <Col className="_col detail-section">
                            <button
                              type="button"
                              className="_row detail-section-header pointer"
                              aria-expanded={openSections.description}
                              onClick={() => handleToggleSection("description")}
                            >
                              <Row className="_row detail-section-title">
                                <Icon
                                  icon="solar:document-text-linear"
                                  className="_svg section-title"
                                />
                                <h6 className="_label section-title">
                                  {t("Description")}
                                </h6>
                              </Row>
                              <Icon
                                icon={
                                  openSections.description
                                    ? "mingcute:up-line"
                                    : "mingcute:down-line"
                                }
                                className="_svg section-chevron"
                              />
                            </button>
                            {openSections.description && (
                              <article
                                className="element _row description"
                                dangerouslySetInnerHTML={{
                                  __html: productInfo?.description || "",
                                }}
                              />
                            )}
                          </Col>
                        )} */}

                          <FinancingSection
                            carPrice={finalPrice}
                            reservationFee={reservationFee}
                            handlingFee={handlingPrice}
                            roadTax={roadTaxPrice}
                            insurance={insurancePrice}
                          />
                        </Col>

                        {!isDesktop && inspectionContent}
                      </Col>
                    )}
                  </Col>
                </Container>
                {!isCarPlate && similarProducts.length > 0 && (
                  <Col className="_col recommendation-section">
                    <h4 className="_label recommendation-title">
                      {t("Still Can’t Decide?")}
                    </h4>
                    <p className="_label recommendation-description">
                      {t("You may also like these similar cars")}
                    </p>
                    <Swiper
                      touchStartPreventDefault={false}
                      className="recommendation-swiper"
                      slidesPerView={width > 1024 ? 4 : "auto"}
                      spaceBetween={15}
                    >
                      {similarProducts.map((product) => (
                        <SwiperSlide key={product.id}>
                          <CommonProduct product={product} type="list" />
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </Col>
                )}
              </Col>
            )}
          </MDBModalBody>
        }
      />
      <ReferAndEarnModal
        onShare={() => handleShare()}
        onCopyLink={() => handleShare(true)}
      />
      <ReserveVehicleModal
        productId={productInfo?.id}
        productName={productInfo?.name}
      />
    </>
  );
}
